import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface CacheStats {
  hits: number
  misses: number
  writes: number
  evictions: number
  errors: number
  hit_rate: number
  memory_items: number
  redis: {
    used_memory?: string
    peak_memory?: string
    connected: boolean
  }
  config: {
    default_ttl: number
    max_memory_items: number
    cache_enabled: boolean
  }
  namespace_ttls: Record<string, number>
}

interface CacheKey {
  key: string
  source: string
  expires_in: number
  created_at: string | null
}

const API_BASE = '/api/cache'

const CacheDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'keys' | 'config' | 'operations'>('overview')
  const [stats, setStats] = useState<CacheStats | null>(null)
  const [keys, setKeys] = useState<CacheKey[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedNamespace, setSelectedNamespace] = useState<string>('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Config edit state
  const [editingTTL, setEditingTTL] = useState<string | null>(null)
  const [newTTL, setNewTTL] = useState<number>(0)

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 5000) // Refresh every 5 seconds
    return () => clearInterval(interval)
  }, [selectedNamespace])

  const loadData = async () => {
    try {
      const [statsRes, keysRes] = await Promise.all([
        fetch(`${API_BASE}/statistics`),
        fetch(`${API_BASE}/keys${selectedNamespace ? `?namespace=${selectedNamespace}` : ''}`)
      ])

      if (statsRes.ok) setStats(await statsRes.json())
      if (keysRes.ok) setKeys(await keysRes.json())
    } catch (err) {
      console.error('Failed to load cache data:', err)
    } finally {
      setLoading(false)
    }
  }

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 3000)
  }

  const handleClearAll = async () => {
    if (!confirm('Are you sure you want to clear all cached data?')) return

    try {
      const res = await fetch(`${API_BASE}/clear`, { method: 'POST' })
      if (res.ok) {
        showMessage('success', 'Cache cleared successfully')
        loadData()
      }
    } catch (err) {
      showMessage('error', 'Failed to clear cache')
    }
  }

  const handleInvalidateNamespace = async (namespace: string) => {
    try {
      const res = await fetch(`${API_BASE}/invalidate/${namespace}`, { method: 'POST' })
      if (res.ok) {
        const data = await res.json()
        showMessage('success', `Invalidated ${data.keys_invalidated} keys in ${namespace}`)
        loadData()
      }
    } catch (err) {
      showMessage('error', 'Failed to invalidate namespace')
    }
  }

  const handleDeleteKey = async (key: string) => {
    const [namespace, ...keyParts] = key.split(':')
    const actualKey = keyParts.join(':')

    try {
      const res = await fetch(`${API_BASE}/delete/${namespace}/${actualKey}`, { method: 'DELETE' })
      if (res.ok) {
        showMessage('success', 'Key deleted')
        loadData()
      }
    } catch (err) {
      showMessage('error', 'Failed to delete key')
    }
  }

  const handleUpdateTTL = async (namespace: string) => {
    try {
      const res = await fetch(`${API_BASE}/namespace/${namespace}/ttl`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ttl: newTTL })
      })

      if (res.ok) {
        showMessage('success', `TTL updated for ${namespace}`)
        setEditingTTL(null)
        loadData()
      }
    } catch (err) {
      showMessage('error', 'Failed to update TTL')
    }
  }

  const handleToggleCache = async () => {
    if (!stats) return

    try {
      const res = await fetch(`${API_BASE}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cache_enabled: !stats.config.cache_enabled })
      })

      if (res.ok) {
        showMessage('success', stats.config.cache_enabled ? 'Cache disabled' : 'Cache enabled')
        loadData()
      }
    } catch (err) {
      showMessage('error', 'Failed to toggle cache')
    }
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    return `${Math.floor(seconds / 3600)}h`
  }

  const styles: Record<string, React.CSSProperties> = {
    container: { minHeight: '100vh', backgroundColor: '#f5f7fa' },
    content: { padding: '32px', maxWidth: '1400px', margin: '0 auto' },
    title: { fontSize: '28px', fontWeight: 700, color: '#1F2937', marginBottom: '8px' },
    subtitle: { color: '#6B7280', marginBottom: '24px' },
    tabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
      borderBottom: '1px solid #E5E7EB',
      paddingBottom: '8px'
    },
    tab: {
      padding: '10px 20px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
      color: '#6B7280',
      borderRadius: '8px 8px 0 0'
    },
    activeTab: { backgroundColor: '#4A90E2', color: 'white' },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    statCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    statValue: { fontSize: '28px', fontWeight: 700, color: '#1F2937' },
    statLabel: { fontSize: '14px', color: '#6B7280', marginTop: '4px' },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '24px'
    },
    cardTitle: { fontSize: '18px', fontWeight: 600, color: '#1F2937', marginBottom: '16px' },
    table: { width: '100%', borderCollapse: 'collapse' as const },
    th: {
      textAlign: 'left' as const,
      padding: '12px 16px',
      borderBottom: '2px solid #E5E7EB',
      fontSize: '12px',
      fontWeight: 600,
      color: '#6B7280',
      textTransform: 'uppercase' as const
    },
    td: { padding: '12px 16px', borderBottom: '1px solid #E5E7EB' },
    input: {
      padding: '8px 12px',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      fontSize: '14px',
      width: '100px'
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#4A90E2',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500
    },
    dangerButton: {
      padding: '8px 16px',
      backgroundColor: '#EF4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500
    },
    secondaryButton: {
      padding: '8px 16px',
      backgroundColor: '#F3F4F6',
      color: '#374151',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: 500
    },
    greenBadge: { backgroundColor: '#D1FAE5', color: '#059669' },
    redBadge: { backgroundColor: '#FEE2E2', color: '#DC2626' },
    blueBadge: { backgroundColor: '#DBEAFE', color: '#2563EB' },
    progressBar: {
      height: '8px',
      backgroundColor: '#E5E7EB',
      borderRadius: '4px',
      overflow: 'hidden',
      marginTop: '8px'
    },
    progressFill: { height: '100%', backgroundColor: '#10B981', transition: 'width 0.3s ease' },
    message: {
      padding: '12px 20px',
      borderRadius: '8px',
      marginBottom: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    select: {
      padding: '8px 12px',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      fontSize: '14px'
    }
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={styles.content}>
          <p>Loading cache data...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <h1 style={styles.title}>Cache Dashboard</h1>
        <p style={styles.subtitle}>Monitor and manage application cache</p>

        {message && (
          <div
            style={{
              ...styles.message,
              backgroundColor: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
              color: message.type === 'success' ? '#059669' : '#DC2626'
            }}
          >
            {message.text}
            <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              &times;
            </button>
          </div>
        )}

        <div style={styles.tabs}>
          {(['overview', 'keys', 'config', 'operations'] as const).map((tab) => (
            <button
              key={tab}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.activeTab : {}) }}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && stats && (
          <>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#10B981' }}>{stats.hit_rate}%</div>
                <div style={styles.statLabel}>Hit Rate</div>
                <div style={styles.progressBar}>
                  <div style={{ ...styles.progressFill, width: `${stats.hit_rate}%` }} />
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.hits.toLocaleString()}</div>
                <div style={styles.statLabel}>Cache Hits</div>
              </div>
              <div style={styles.statCard}>
                <div style={{ ...styles.statValue, color: '#F59E0B' }}>{stats.misses.toLocaleString()}</div>
                <div style={styles.statLabel}>Cache Misses</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.writes.toLocaleString()}</div>
                <div style={styles.statLabel}>Writes</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.evictions.toLocaleString()}</div>
                <div style={styles.statLabel}>Evictions</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.memory_items}</div>
                <div style={styles.statLabel}>Memory Items</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Redis Status</h3>
                {stats.redis.connected ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ ...styles.badge, ...styles.greenBadge }}>Connected</span>
                    </div>
                    <p style={{ color: '#6B7280', margin: '8px 0' }}>
                      Used Memory: {stats.redis.used_memory || 'N/A'}
                    </p>
                    <p style={{ color: '#6B7280', margin: '8px 0' }}>
                      Peak Memory: {stats.redis.peak_memory || 'N/A'}
                    </p>
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ ...styles.badge, ...styles.redBadge }}>Disconnected</span>
                    <span style={{ color: '#6B7280' }}>Using memory cache fallback</span>
                  </div>
                )}
              </div>

              <div style={styles.card}>
                <h3 style={styles.cardTitle}>Cache Status</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <span
                    style={{
                      ...styles.badge,
                      ...(stats.config.cache_enabled ? styles.greenBadge : styles.redBadge)
                    }}
                  >
                    {stats.config.cache_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button onClick={handleToggleCache} style={styles.secondaryButton}>
                    {stats.config.cache_enabled ? 'Disable' : 'Enable'} Cache
                  </button>
                </div>
                <p style={{ color: '#6B7280', margin: '8px 0' }}>
                  Default TTL: {formatDuration(stats.config.default_ttl)}
                </p>
                <p style={{ color: '#6B7280', margin: '8px 0' }}>
                  Max Memory Items: {stats.config.max_memory_items.toLocaleString()}
                </p>
              </div>
            </div>
          </>
        )}

        {activeTab === 'keys' && (
          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>Cached Keys</h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <select
                  value={selectedNamespace}
                  onChange={(e) => setSelectedNamespace(e.target.value)}
                  style={styles.select}
                >
                  <option value="">All Namespaces</option>
                  {stats && Object.keys(stats.namespace_ttls).map((ns) => (
                    <option key={ns} value={ns}>{ns}</option>
                  ))}
                </select>
                <button onClick={loadData} style={styles.secondaryButton}>Refresh</button>
              </div>
            </div>

            {keys.length === 0 ? (
              <p style={{ color: '#6B7280' }}>No cached keys found</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Key</th>
                    <th style={styles.th}>Source</th>
                    <th style={styles.th}>Expires In</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key, idx) => (
                    <tr key={idx}>
                      <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '13px' }}>{key.key}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, ...(key.source === 'redis' ? styles.blueBadge : styles.greenBadge) }}>
                          {key.source}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {key.expires_in > 0 ? formatDuration(key.expires_in) : 'Expired'}
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => handleDeleteKey(key.key)}
                          style={styles.dangerButton}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'config' && stats && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Namespace TTL Configuration</h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Namespace</th>
                  <th style={styles.th}>TTL</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.namespace_ttls).map(([namespace, ttl]) => (
                  <tr key={namespace}>
                    <td style={{ ...styles.td, fontWeight: 500 }}>{namespace}</td>
                    <td style={styles.td}>
                      {editingTTL === namespace ? (
                        <input
                          type="number"
                          value={newTTL}
                          onChange={(e) => setNewTTL(Number(e.target.value))}
                          style={styles.input}
                        />
                      ) : (
                        <span style={{ ...styles.badge, ...styles.blueBadge }}>
                          {formatDuration(ttl)}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {editingTTL === namespace ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => handleUpdateTTL(namespace)} style={styles.button}>
                            Save
                          </button>
                          <button onClick={() => setEditingTTL(null)} style={styles.secondaryButton}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => {
                              setEditingTTL(namespace)
                              setNewTTL(ttl)
                            }}
                            style={styles.secondaryButton}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleInvalidateNamespace(namespace)}
                            style={styles.dangerButton}
                          >
                            Invalidate
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'operations' && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Cache Operations</h3>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div style={{ padding: '16px', backgroundColor: '#FEF3C7', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#92400E' }}>Clear All Cache</h4>
                <p style={{ color: '#78350F', marginBottom: '12px' }}>
                  This will remove all cached data from both Redis and memory cache.
                </p>
                <button onClick={handleClearAll} style={styles.dangerButton}>
                  Clear All Cache
                </button>
              </div>

              <div style={{ padding: '16px', backgroundColor: '#F3F4F6', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#374151' }}>Cache Warming</h4>
                <p style={{ color: '#6B7280', marginBottom: '12px' }}>
                  Pre-populate cache with frequently accessed data. This feature is available via API.
                </p>
                <code style={{ display: 'block', padding: '8px', backgroundColor: 'white', borderRadius: '4px', fontSize: '13px' }}>
                  POST /api/cache/warm/{'<namespace>'} {'{'} items: [{'{'} key, value, ttl {'}'}] {'}'}
                </code>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CacheDashboard
